import { coreActions } from '@sonar-software/sonar-core';
import { getPeriscopeSummaryRouteUri } from '@sonar-software/sonar-ts-lib';
import { useAction } from '../useAction';
import { useApiResource } from '../useApiResource';
import { useTagReference } from '../tags/useTagReference';
import { useUpdateSonarObjectReference } from '../references/useUpdateSonarObjectReference';

import { PeriscopeSummary, SonarObjectType } from '@sonar-software/sonar-ts-types';
import { useEffect, useMemo } from 'react';

export interface UsePeriscopeSummaryResult {
	loading: boolean;
	summary?: PeriscopeSummary;
}

export function usePeriscopeSummary(
	sonarObjectId: number | undefined,
	sonarObjectType: SonarObjectType
): UsePeriscopeSummaryResult {
	const tag = useTagReference(sonarObjectId ?? 0);
	const loadApplications = useAction(coreActions.requestGetCoreSonarObjectsApplications);

	const isTag = SonarObjectType.SonarTag === sonarObjectType;

	const targetUri = useMemo(
		() => getPeriscopeSummaryRouteUri(sonarObjectType, sonarObjectId),
		[ sonarObjectId, sonarObjectType ]
	);

	const { loading, resource } = useApiResource<PeriscopeSummary>(targetUri);

	const { update } = useUpdateSonarObjectReference();

	useEffect(() => {
		if (sonarObjectId) {
			loadApplications({ sonarObjectId });
		}
	}, [ loadApplications, sonarObjectId ]);

	useEffect(() => {
		if (resource && sonarObjectId) {
			update({
				displayName: resource.displayName,
				hasFieldSpyMetadata: resource.hasFieldSpyMetadata,
				internalName: resource.internalName,
				isActive: resource.isActive,
				parentDisplayName: resource.parentDisplayName,
				parentSonarObjectId: resource.parentSonarObjectId,
				relatedCounts: resource.relatedCounts,
				salesforce: {
					description: resource.description,
					helpText: resource.helpText,
					isCustom: resource.isCustom,
					managedPackage: resource.managedPackage
				},
				sonarObjectId,
				sonarObjectType: sonarObjectType,
				sourceUrl: resource.sourceUrl,
				tags: resource.tagIds,
				versionNumber: resource.versionNumber
			});

			if (resource.parentSonarObjectId) {
				update({
					displayName: resource.parentDisplayName,
					sonarObjectId: resource.parentSonarObjectId
				});
			}
		}
	}, [ resource, sonarObjectId, sonarObjectType, update ]);

	if (resource && !resource.sonarObjectId && sonarObjectId) {
		resource.sonarObjectId = sonarObjectId;
	}

	if (resource && !resource?.sonarObjectType && sonarObjectType) {
		resource.sonarObjectType = sonarObjectType;
	}

	const managedResource = useMemo(() => {
		if (isTag) {
			return {
				displayName: tag.name,
				sonarObjectId: sonarObjectId,
				sonarObjectType: SonarObjectType.SonarTag
			} as PeriscopeSummary;
		}

		return resource;
	}, [ isTag, resource, sonarObjectId, tag.name ]);

	return {
		loading: isTag ? false : loading,
		summary: managedResource
	};
}
